import { useContext, useEffect, useState } from "react";
import { AppToast } from "../../../commanComponents/AppToast";
import {
  Box,
  Container,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { colors } from "../../../style/color/colors";

import EstimatorCard from "../EstimatorCard";
import DashboardPieChart from "../DashboardPieChart";
import { Info } from "@mui/icons-material";
import { String } from "../../../constants/Strings";
import TotalAssetAmountCard from "./TotalAssetAmountCard";
import CryptoJS from "crypto-js";
import { useAgentDashboardService } from "../../../api/apiEndpoints/AgentFetch";
import SavingGoalCards from "./SavingGoalCards";
import { formatToIndianCurrency } from "../../../logic/CurrencyFormatter";
import { fonts } from "../../../style/fonts/fonts";
import { gaCustomEvent } from "../../../logic/CommonFunctions";
import { OTPVerifyContext } from "../../../Hooks/context/OTPVeriryContext";
import { ENCRYPTION_KEY, ENCRYPTION_IV } from "../../../config/env";

const AgentDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalAssets: "",
    assets: [],
    totalNetWorth: "",
    totalLiabilities: "",
    monthlySaving: "",
    monthlySalary: "",
    monthlyEmis: "",
    insuranceEstimator: {
      currentLifeCoverage: "",
      idealTermLifeInsurance: "",
      percentage: "",
    },
    goals: [],
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({
    message: "",
    severity: "",
  });

  const { verifyData } = useContext(OTPVerifyContext);

  const SECRET_KEY = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY); 
  const IV = CryptoJS.enc.Utf8.parse(ENCRYPTION_IV);

  const URL = window.location.href;

  const decryptData = (encryptedData) => {
    try {
      if (!encryptedData)
        throw new Error(String.OnbordingStr.noDecryptedErrorMessage);

      const decodedData = CryptoJS.enc.Base64.parse(encryptedData);

      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: decodedData },
        SECRET_KEY,
        {
          iv: IV,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      );

      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      if (!decryptedText)
        throw new Error(String.OnbordingStr.DecryptedErrorMessage);

      return JSON.parse(decryptedText);
    } catch (error) {
      return null;
    }
  };
  const res = decryptData(URL.match(/view=(.+)/)[1]);
  const { customerId, token, customerName } = res;

  const { fetchDashboardDetails } = useAgentDashboardService();

  useEffect(() => {
    const fetchDashboardDetail = async () => {
      try {
        const response = await fetchDashboardDetails({
          body: {
            userId: customerId,
          },
          token,
        });

        if (response.httpCode === 200) {
          setDashboardData(response.response);
          setSnackbarMessage({
            message: String.Dashboard.mainDashboard.dashboardDataFetchSuccess,
            severity: String.successSeverity,
          });
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage({
            message: String.Dashboard.expiredLink,
            severity: String.ErrorSeverity,
          });
          setSnackbarOpen(true);
        }
      } catch (error) {
        setSnackbarMessage({
          message: String.Dashboard.mainDashboard.dashboardDataFetchError,
          severity: String.ErrorSeverity,
        });
        setSnackbarOpen(true);
      }
    };
    fetchDashboardDetail();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    gaCustomEvent(
      String.gaEventName.acrDashboard,
      String.gaEventClickName.acrDashboardHome,
      verifyData.customerId,
    );
  }, [verifyData.customerId]);

  return (
    <Stack
      sx={{
        backgroundColor: colors.SecondaryMidnightBlue,
        height: "100vh",
        overflowY: "aoto",
      }}
    >
      <Container sx={{}}>
        <Box
          sx={{
            display: "flex",
            mt: "40px",
          }}
        >
          <Typography
            sx={{
              color: colors.PrimaryWhite,
              fontSize: "24px",
              fontFamily: fonts.manrope,
              fontWeight: 700,
              mt: "5px",
            }}
          >
            {String.Dashboard.mainDashboard.heading}
          </Typography>
        </Box>
      </Container>
      <Box sx={{ ml: "30px" }}>
        <Box
          sx={{
            display: "flex",

            justifyContent: "space-between",
            mt: "30px",
            pr: 2,
            pl: 1,
          }}
        >
          <Typography
            sx={{
              color: colors.PrimaryWhite,
              fontSize: "18px",
              fontFamily: fonts.manrope,
            }}
          >{`${customerName}`}</Typography>
          <Typography
            sx={{
              color: colors.PrimaryWhite,
              fontSize: "18px",
              fontFamily: fonts.manrope,
            }}
          >{`Custm ID:${customerId}`}</Typography>
        </Box>
        <Divider sx={{ mt: "20px", color: colors.PrimaryWhite }} />
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Typography sx={{ fontSize: 12, color: colors.PrimaryWhite }}>
            {String.Dashboard.mainDashboard.TotalAssets}
          </Typography>
          <Tooltip
            title={
              String.Dashboard.totolAMountCardTooltip.agentdashboardTooltip
            }
            sx={{ position: "relative", ml: "5px" }}
            enterTouchDelay={0}
          >
            <IconButton>
              <Info
                sx={{
                  width: 12,
                  height: 12,
                  fontSize: "1rem",
                  color: colors.PrimaryWhite,
                  fontFamily: fonts.manrope,
                  fontWeight: "700",
                  left: 0,
                  top: 0,
                  position: "absolute",
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          sx={{
            color: colors.PrimaryWhite,
            fontSize: 25,
            fontFamily: fonts.manrope,
            fontWeight: "700",
            mt: "5px",
          }}
        >
          {formatToIndianCurrency(dashboardData.totalAssets)}
        </Typography>
      </Box>
      <Box
        sx={{
          maxWidth: "100%",
          margin: "0 auto",
          backgroundColor: colors.indigo,
          color: colors.PrimaryWhite,
          p: 1,
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          "@media (max-width: 600px)": {
            p: 1,
            borderRadius: 2,
          },
        }}
      >
        <DashboardPieChart assets={dashboardData.assets || []} />
      </Box>

      <>
        <Box
          sx={{
            backgroundColor: "#003A70",
            color: colors.PrimaryWhite,
            p: 2,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <TotalAssetAmountCard
            monthlySaving={dashboardData.monthlySaving}
            monthlyIncome={dashboardData.monthlySalary}
          />
        </Box>
        <Typography
          sx={{
            color: colors.PrimaryWhite,
            fontSize: 13,
            fontFamily: fonts.manrope,
            fontWeight: "500",
            mb: "30px",
            ml: "30px",
          }}
        >
          {String.Dashboard.mainDashboard.Alldataareindicative}
        </Typography>

        <Container
          sx={{
            background: "white",
            borderTopLeftRadius: 40,
            borderTopRightRadius: 40,
            overflowY: "aoto",
          }}
        >
          {" "}
          <EstimatorCard
            lifeCoverage={dashboardData.insuranceEstimator.currentLifeCoverage}
            termLifeInsurance={
              dashboardData.insuranceEstimator.idealTermLifeInsurance
            }
            percentage={dashboardData.insuranceEstimator.percentage}
          />
          <SavingGoalCards goals={dashboardData.goals} />
        </Container>
      </>

      <AppToast
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
      />
    </Stack>
  );
};

export default AgentDashboard;
